import AutoAwesomeIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DnsIcon from "@mui/icons-material/DnsOutlined";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { type FC, type ReactNode, useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { MapThumb } from "src/components/Solutions/MapThumb";
import { RackIllustration } from "src/explore/RackIllustration";

const SLIDES = ["advisor", "map", "showcases"] as const;
type Slide = (typeof SLIDES)[number];
const ADVANCE_MS = 6000;
const VISUAL_HEIGHT = 300;
const DOT_SIZE = 12;

const Bubble: FC<{ mine?: boolean; children: ReactNode }> = ({ mine, children }) => (
	<Box
		sx={{
			alignSelf: mine ? "flex-end" : "flex-start",
			maxWidth: "80%",
			px: 1.75,
			py: 1,
			borderRadius: mine ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
			bgcolor: mine ? "brand.userBubble" : "background.paper",
			border: mine ? 0 : 1,
			borderColor: "divider",
			fontSize: 14
		}}>
		{children}
	</Box>
);

/** Decorative previews of the product, one per slide. */
const SlideVisual: FC<{ slide: Slide }> = ({ slide }) => {
	const { t } = useTranslation();
	if (slide === "advisor") {
		return (
			<Stack
				spacing={1.25}
				sx={{ height: VISUAL_HEIGHT, p: 3, bgcolor: "background.default", justifyContent: "center" }}>
				<Stack direction="row" spacing={1} sx={{ alignItems: "center", color: "primary.main" }}>
					<AutoAwesomeIcon fontSize="small" />
					<Typography variant="h5" component="span">
						{t("nav.advisor")}
					</Typography>
				</Stack>
				<Bubble>{t("register.tour.advisorQuestion")}</Bubble>
				<Bubble mine>{t("register.tour.advisorAnswer")}</Bubble>
				<Bubble>{t("register.tour.advisorReply")}</Bubble>
			</Stack>
		);
	}
	if (slide === "map") {
		return (
			<Box sx={{ position: "relative", height: VISUAL_HEIGHT }}>
				<MapThumb sx={{ position: "absolute", inset: 0 }} />
				<Stack
					direction="row"
					spacing={1}
					sx={{
						position: "absolute",
						left: "50%",
						top: "45%",
						transform: "translate(-50%, -50%)",
						alignItems: "center",
						px: 1.5,
						py: 0.75,
						borderRadius: "999px",
						bgcolor: "primary.main",
						color: "primary.contrastText",
						fontSize: 13,
						fontWeight: 600
					}}>
					<DnsIcon sx={{ fontSize: 16 }} />
					<span>{t("register.tour.mapPin")}</span>
				</Stack>
			</Box>
		);
	}
	return <RackIllustration height={VISUAL_HEIGHT} />;
};

/**
 * Wireframe 2:635 left column carousel: three slides with dots. Advances on its own unless the
 * visitor prefers reduced motion; the dots switch slides directly.
 */
export const ProductTour: FC = () => {
	const { t } = useTranslation();
	const [index, setIndex] = useState(0);
	const slide = SLIDES[index] ?? "advisor";

	useEffect(() => {
		if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
		const timer = window.setInterval(() => setIndex(current => (current + 1) % SLIDES.length), ADVANCE_MS);
		return () => window.clearInterval(timer);
	}, []);

	return (
		<Box component="section" aria-roledescription="carousel" aria-label={t("register.tour.label")}>
			<Box sx={{ borderRadius: "16px", overflow: "hidden", border: 1, borderColor: "divider" }}>
				<SlideVisual slide={slide} />
			</Box>
			<Stack direction="row" spacing={1.25} sx={{ justifyContent: "center", mt: 2 }}>
				{SLIDES.map((key, position) => (
					<Dot key={key} position={position} active={position === index} onSelect={setIndex} />
				))}
			</Stack>
			<Typography variant="body1" sx={{ mt: 2, color: "brand.body" }} aria-live="polite">
				<Trans i18nKey={`register.tour.${slide}`} components={{ strong: <strong /> }} />
			</Typography>
		</Box>
	);
};

const Dot: FC<{ position: number; active: boolean; onSelect: (position: number) => void }> = ({
	position,
	active,
	onSelect
}) => {
	const { t } = useTranslation();
	const handleClick = useCallback(() => onSelect(position), [onSelect, position]);
	return (
		<ButtonBase
			onClick={handleClick}
			aria-label={t("register.tour.slide", { number: position + 1 })}
			aria-current={active}
			sx={{
				width: DOT_SIZE,
				height: DOT_SIZE,
				borderRadius: "50%",
				bgcolor: active ? "primary.main" : "brand.outline"
			}}
		/>
	);
};
